import React from 'react';
import { createPortal } from 'react-dom';

import { useCurrentTheme } from 'app/shared/theme';
import { useIsBreakpointSize } from 'app/shared/utils';
import { getPosition } from 'app/shared/utils/usePosition';
import { Popper } from 'app/shared/components/atoms';
import { Box } from 'app/shared/components/atoms';

interface Props {
  isSearchable?: boolean;
  optionsLength: number;
  selectRef: React.RefObject<any>;
  optionsRef: React.RefObject<any>;
  backgroundColor: string;
  zIndex: number;
}

export const OptionsContainer: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  isSearchable,
  optionsLength,
  selectRef,
  backgroundColor,
  optionsRef,
  zIndex,
}) => {
  const { isMobile } = useIsBreakpointSize();
  const { colors, borderRadius } = useCurrentTheme();

  const sharedContainerProps = {
    backgroundColor,
    w: `calc(${selectRef.current?.clientWidth}px + 2px)`,
    h: 'fit-content',
    maxHeight: '300px',
  };

  const searchableContainerProps = {
    bt: colors.accent.primaryAccent.base,
    bw: '2px',
  };
  const searchableVerticalOffset = 4;

  /*
    The Popper component doesn't behave as expected on mobile browsers when an input
    is focused and the soft keyboard is triggered, displaying the select options
    in the wrong position (it bounces up to cover the input field so you can't see what you type).
    This alternative uses a dumber component that requires fewer calculations and is a bit less flexible,
    in an attempt to give mobile users a better experience when using searchable select fields.
  */
  if (isMobile && isSearchable) {
    const anchorRefPositionAttributes = getPosition(selectRef.current);
    const offsetTop = selectRef.current.offsetTop + searchableVerticalOffset;
    const top = `${offsetTop + anchorRefPositionAttributes.height}px`;

    return (
      <Box
        position="absolute"
        zIndex={zIndex}
        top={top}
        w={`${anchorRefPositionAttributes.width}px`}
        ref={optionsRef}
      >
        <Box
          data-qaid="select-options"
          {...sharedContainerProps}
          {...searchableContainerProps}
        >
          {children}
        </Box>
      </Box>
    );
  }

  const sharedPopperProps = {
    anchorEl: selectRef,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    keepInViewPort: false,
    flip: false,
    showDiamond: false,
    reactToChange: optionsLength,
    zIndex,
  };

  if (isSearchable) {
    const offsetTop = searchableVerticalOffset;

    return createPortal(
      <Popper {...sharedPopperProps} offset={{ vertical: offsetTop }}>
        <Box
          data-qaid="select-options"
          ref={optionsRef}
          {...sharedContainerProps}
          {...searchableContainerProps}
        >
          {children}
        </Box>
      </Popper>,
      document.body
    );
  }

  return createPortal(
    <Popper {...sharedPopperProps} offset={{ vertical: 0 }}>
      <Box
        data-qaid="select-options"
        br={colors.accent.primaryAccent.base}
        bb={colors.accent.primaryAccent.base}
        bl={colors.accent.primaryAccent.base}
        borderRadiusTopLeft="0"
        borderRadiusTopRight="0"
        borderRadiusBottomLeft={borderRadius.input}
        borderRadiusBottomRight={borderRadius.input}
        ref={optionsRef}
        {...sharedContainerProps}
      >
        {children}
      </Box>
    </Popper>,
    document.body
  );
};
